<template>
  <DxPopup
    :visible="visible"
    :hide-on-outside-click="false"
    :show-close-button="false"
    :drag-enabled="false"
    :resize-enabled="false"
    :shading="false"
    :width="820"
    :height="580"
    @hidden="$emit('hidden')"
  >
    <template #title>
      <div class="lot-selector-title-block">
        <span class="lot-selector-title-main">{{ $t("lot_items.available_title") }}</span>
        <span v-if="productName" class="lot-selector-title-sub"
          >{{ productName }}<template v-if="measureUnitSymbol"> · {{ measureUnitSymbol }}</template></span
        >
      </div>
    </template>
    <template #content>
      <div class="lot-selector-wrap">
        <div v-if="loading" class="lot-selector-loading">{{ $t("lot_items.loading") }}</div>
        <div v-else-if="!items.length" class="lot-selector-empty">{{ $t("lot_items.available_empty") }}</div>
        <DxDataGrid
          v-else
          ref="grid"
          class="lot-selector-grid smagria-grid"
          :data-source="items"
          key-expr="group_key"
          :show-borders="true"
          :column-auto-width="true"
          :remote-operations="false"
          :hover-state-enabled="true"
          :selected-row-keys="selectedKeys"
          :element-attr="gridAttr"
          height="360"
          @selection-changed="onSelectionChanged"
          @row-click="onRowClick"
        >
          <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="allPages" />
          <DxFilterRow :visible="true" />
          <DxScrolling mode="virtual" />
          <DxSorting mode="multiple" />

          <DxColumn data-field="lot" :caption="$t('lot_items.lot')" :allow-editing="false" :min-width="140" />
          <DxColumn
            data-field="expiration_date_display"
            :caption="$t('common.expiration_date')"
            :allow-editing="false"
            :allow-filtering="false"
            :allow-sorting="false"
            :min-width="130"
          />
          <DxColumn
            data-field="available_quantity"
            :caption="$t('lot_items.available_qty')"
            data-type="number"
            :allow-editing="false"
            :allow-filtering="false"
            alignment="right"
            :min-width="120"
          />
          <DxColumn
            data-field="measure_unit.name"
            :caption="$t('common.measure_unit')"
            :allow-editing="false"
            :allow-filtering="false"
            :allow-sorting="false"
            :min-width="100"
          />
        </DxDataGrid>

        <small v-if="error" class="text-danger lot-selector-error">{{ error }}</small>

        <div class="lot-selector-actions">
          <SmagriaButton :label="$t('common.cancel')" customClass="secondary border" :width="'130px'" :action="hide" />
          <SmagriaButton :label="$t('common.continue')" customClass="primary" :width="'130px'" :action="onContinue" />
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { DxPopup } from "devextreme-vue/popup";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxScrolling, DxSorting } from "devextreme-vue/data-grid";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import { mapAvailableLotGroups } from "~/helpers/stockHelper";
import moment from "moment";

export default {
  name: "LotAvailableSelectorDialog",
  components: { DxPopup, DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxScrolling, DxSorting, SmagriaButton },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productUuid: {
      type: String,
      default: null,
    },
    warehouseUuid: {
      type: String,
      default: null,
    },
    movementDate: {
      type: String,
      default: null,
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
    productName: {
      type: String,
      default: "",
    },
    transactionUuid: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      selectedKeys: [],
      error: "",
      gridAttr: { class: "smagria-grid lot-available-grid" },
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
  },
  watch: {
    visible(val) {
      if (val) {
        this.error = "";
        this.selectedKeys.splice(0, this.selectedKeys.length);
        this.fetchItems();
      }
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    async fetchItems() {
      if (!this.productUuid || !this.warehouseUuid || !this.movementDate) {
        this.items = [];
        return;
      }
      this.loading = true;
      try {
        const response = await this.getSmStockMovementItemsAvailableSum({
          product_uuid: this.productUuid,
          warehouse_uuid: this.warehouseUuid,
          movement_date: this.movementDate,
          transaction_uuid: this.transactionUuid,
          enablePagination: 0,
          limit: 99999,
        });
        const raw = response?.data?.data || response?.data || [];
        this.items = mapAvailableLotGroups(raw).map((group) => ({
          ...group,
          expiration_date_display: this.formatDate(group.expiration_date),
        }));
      } catch (e) {
        this.items = [];
        this.error = this.$t("error_alert_text");
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      const m = moment(value);
      return m.isValid() ? m.format(this.getDefaultDateFormat || "DD.MM.YYYY") : value;
    },
    onSelectionChanged(e) {
      this.selectedKeys = Array.isArray(e?.selectedRowKeys) ? [...e.selectedRowKeys] : [];
      this.error = "";
    },
    onRowClick(e) {
      if (e.rowType !== "data") return;
      const rowKey = e.key;
      const selectedIndex = this.selectedKeys.indexOf(rowKey);
      if (selectedIndex === -1) {
        this.selectedKeys.push(rowKey);
      } else {
        this.selectedKeys.splice(selectedIndex, 1);
      }
      this.$refs.grid.instance.selectRows(this.selectedKeys, false);
      this.error = "";
    },
    hide() {
      this.$emit("hidden");
    },
    onContinue() {
      if (!this.selectedKeys.length) {
        this.error = this.$t("lot_items.select_required");
        return;
      }
      const selected = this.items.filter((item) => this.selectedKeys.includes(item.group_key));
      this.$emit("continue", selected);
    },
  },
};
</script>

<style scoped>
.lot-selector-title-block {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.lot-selector-title-main {
  font-size: 17px;
  font-weight: 700;
  color: #323232;
}
.lot-selector-title-sub {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.lot-selector-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6px 8px 8px;
  gap: 10px;
}
.lot-selector-loading,
.lot-selector-empty {
  text-align: center;
  color: #888;
  padding: 40px 0;
  font-size: 14px;
}
.lot-selector-error {
  display: block;
  margin-top: 2px;
}
.lot-selector-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
}
</style>
